import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link, NavLink } from 'react-router-dom'

import { ConnectWallet } from 'modules/ConnectWallet/ConnectWallet'
import { ROUTES } from 'utils/route-constants'
import { Settings } from 'modules/Settings/Settings'
import { Typography } from 'components/Typography/Typography'
import { IconHome } from 'components/Icons/IconHome'
import { IconVaults } from 'components/Icons/IconVaults'
import { IconLPs } from 'components/Icons/IconLPs'
import { useAppTheme } from 'contexts/ThemeProviderContext'
import { TelegramNotifications } from 'modules/TelegramNotifications/TelegramNotifications'
import { IconDashboard } from 'components/Icons/IconDashboard'
import { isDev } from 'utils/env'
import { ViewAsWallet } from 'modules/ViewAsWallet/ViewAsWallet'
import { useIsMobile } from 'utils/useIsMobile'
import { useGlobalBanner } from 'utils/useGlobalBanner'
import { GLOBAL_BANNER_HEIGHT } from 'components/GlobalBanner/GlobalBanner'

export const HEADER_ID = 'app-header'

const Container = styled.div<{ $scrolled: boolean; $hasBanner: boolean }>`
  position: fixed;
  top: ${p => (p.$hasBanner ? `${GLOBAL_BANNER_HEIGHT}px` : '0')};
  left: 0;
  width: 100%;
  display: flex;
  padding: 20px 0px;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
  ${p =>
    p.$scrolled &&
    `
    background: ${p.theme.colors.header.scrolled};
    backdrop-filter: blur(25px);
  `}

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 20px;
    width: calc(100% - 40px);
  }
`

const BottomNavigation = styled.div`
  position: fixed;
  bottom: 24px;
  left: 24px;
  width: calc(100% - 48px);
  border-radius: 100px;
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
  height: 54px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const BottomNavigationItem = styled.div`
  text-align: center;
  padding: 12px;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: auto;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
`

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const StyledLink = styled(NavLink)`
  text-decoration: none;
  font-size: ${p => p.theme.typography.size.Description};
  font-weight: ${p => p.theme.typography.weight.Light};
  color: ${p => p.theme.colors.text.secondary};
  &.active {
    color: ${p => p.theme.colors.text.primary};
    font-weight: ${p => p.theme.typography.weight.SemiBold};
  }
`

const LogoImg = styled.img`
  width: 171px:
  height: 26px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 125px;
    height: 19px;
  }
`
export const Header = () => {
  const [scrolled, setScrolled] = useState(false)

  const isMobile = useIsMobile()
  const { isEnabled: hasBanner } = useGlobalBanner()

  const { theme } = useAppTheme()

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrolled])

  return (
    <>
      <Container $scrolled={scrolled} $hasBanner={hasBanner} id={HEADER_ID}>
        <MaxWidthContainer>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <LogoImg
              src={theme === 'dark' ? '/logo-title-header-dark.svg' : '/logo-title-header.svg'}
              width="171"
              height="26"
              alt="KAI"
            />
          </Link>
          <NavigationContainer>
            <StyledLink to="/">Home</StyledLink>
            <StyledLink to={`/${ROUTES.VAULTS}`}>Vaults</StyledLink>
            <StyledLink to={`/${ROUTES.LP_VAULTS}`}>LP Vaults</StyledLink>
            <StyledLink to={`/${ROUTES.LEADERBOARD}`}>Leaderboard</StyledLink>
            {/* <StyledLink to={`/${ROUTES.DASHBOARD}`}>Dashboard</StyledLink> */}
          </NavigationContainer>
          <RightContainer>
            <TelegramNotifications />
            <Settings />
            <ConnectWallet />
            {isDev() && !isMobile && <ViewAsWallet />}
          </RightContainer>
        </MaxWidthContainer>
      </Container>
      <BottomNavigation>
        <BottomNavigationItem>
          <Typography variant="caption" fontWeight="regular">
            <StyledLink to="/">
              <IconHome />
              <div>Home</div>
            </StyledLink>
          </Typography>
        </BottomNavigationItem>
        <BottomNavigationItem>
          <Typography variant="caption" fontWeight="regular">
            <StyledLink to={`/${ROUTES.VAULTS}`}>
              <IconVaults />
              <div>Vaults</div>
            </StyledLink>
          </Typography>
        </BottomNavigationItem>
        <BottomNavigationItem>
          <Typography variant="caption" fontWeight="regular">
            <StyledLink to={`/${ROUTES.LP_VAULTS}`}>
              <IconLPs />
              <div>LPs</div>
            </StyledLink>
          </Typography>
        </BottomNavigationItem>
        <BottomNavigationItem>
          <Typography variant="caption" fontWeight="regular">
            <StyledLink to={`/${ROUTES.LEADERBOARD}`}>
              <IconDashboard />
              <div>Leaderboard</div>
            </StyledLink>
          </Typography>
        </BottomNavigationItem>
        {/* <BottomNavigationItem>
          <Typography variant="caption" fontWeight="regular">
            <StyledLink to={`/${ROUTES.DASHBOARD}`}>
              <IconDashboard />
              <div>Dashboard</div>
            </StyledLink>
          </Typography>
        </BottomNavigationItem> */}
      </BottomNavigation>
    </>
  )
}
